import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, FileText, Loader, RefreshCw } from 'lucide-react';
import { BackButton } from '../../../../common/BackButton';
import { ScanTextInput } from '../../../../common/inputs';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { docInterpretationService } from '../../../../../services/docInterpretationService';
import type {
  DocInterpretationLine,
  DocInterpretationResult,
  DocInterpretationSettings,
} from '../../../../../types/docInterpretation';
import { DocUploadForm, PreselectedOrder } from './DocUploadForm';
import { DocLineDetail } from './DocLineDetail';
import { PdfPageView } from './PdfPageView';
import { lineAnchors } from './docInterpretationTextSnap';
import { approvalSummary, canApprove, effectiveQuantity, groupLinesByOrder, lineChoices, openChecks, parseRegion, statusClasses } from './docInterpretationHelpers';

interface DocInterpretationViewProps {
  preselectedOrders: PreselectedOrder[];
  onClose: () => void;
  /** Åpner en tolking som finnes fra før (fra loggen «Tolkede dokumenter»). */
  initialResultId?: number;
}

const fmtDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString('nb-NO') : '';

const fmtDateTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString('nb-NO', { dateStyle: 'short', timeStyle: 'short' }) : '';

const fmt = (value: number | null | undefined) =>
  value == null ? '–' : Number(value).toLocaleString('nb-NO', { maximumFractionDigits: 4 });

/**
 * «Les inn leverandørdokument» under Administrer bestillinger (TW 36423840):
 * opplasting (punkt 4–6), oversikt (punkt 14), detaljer med originaldokumentet ved siden av og
 * uthevet linje (punkt 15), avgjørelser (punkt 16–19) og OK-knappen (punkt 20–21).
 */
export const DocInterpretationView: React.FC<DocInterpretationViewProps> = ({ preselectedOrders, onClose, initialResultId }) => {
  const { t } = useTranslation();
  const p = 'manager.modules.workingOrders.docInterpretation';

  const [settings, setSettings] = useState<DocInterpretationSettings | null>(null);
  const [result, setResult] = useState<DocInterpretationResult | null>(null);
  const [lastUpload, setLastUpload] = useState<{ file: File; documentType?: 'Invoice' | 'PackingSlip'; orderNos: number[] } | null>(null);
  const [pdf, setPdf] = useState<Blob | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supplierInput, setSupplierInput] = useState('');
  const [arrivalOverride, setArrivalOverride] = useState<boolean | null>(null);
  const [confirmOk, setConfirmOk] = useState(false);
  const [reinterpreting, setReinterpreting] = useState(false);
  const [detailDirty, setDetailDirty] = useState(false);
  const [showAllWarnings, setShowAllWarnings] = useState(false);

  useEffect(() => {
    docInterpretationService.getSettings().then(setSettings).catch(err => setError(err.message));
  }, []);

  useEffect(() => {
    if (initialResultId == null) return;
    docInterpretationService.get(initialResultId)
      .then(next => { setResult(next); setSupplierInput(next.supplier.supplierNo ?? ''); })
      .catch(err => setError(err.message));
  }, [initialResultId]);

  const resultId = result?.id ?? null;
  useEffect(() => {
    if (resultId == null) { setPdf(null); return; }
    docInterpretationService.getFile(resultId).then(setPdf).catch(err => setError(err.message));
  }, [resultId]);

  const showResult = useCallback((next: DocInterpretationResult) => {
    setResult(next);
    setSupplierInput(next.supplier.supplierNo ?? '');
  }, []);

  const run = useCallback(async (action: () => Promise<DocInterpretationResult>) => {
    setBusy(true);
    setError(null);
    try {
      showResult(await action());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [showResult]);

  const interpret = async (file: File, documentType: 'Invoice' | 'PackingSlip' | undefined, orderNos: number[], reinterpret = false) => {
    setLastUpload({ file, documentType, orderNos });
    setSelectedLineId(null);
    setReinterpreting(reinterpret);
    try {
      await run(() => docInterpretationService.interpret({ file, documentType, orderNos, reinterpret }));
    } finally {
      setReinterpreting(false);
    }
  };

  const selectLine = (line: DocInterpretationLine) => {
    setSelectedLineId(line.id);
    if (line.pageNo) setPage(line.pageNo);
  };

  const selectedLine = result?.lines.find(l => l.id === selectedLineId) ?? null;
  const groups = useMemo(() => groupLinesByOrder(result?.lines ?? []), [result]);
  const arrivalRegistration = arrivalOverride ?? result?.arrivalRegistrationOverride ?? settings?.arrivalRegistrationDefault ?? true;
  const approved = result?.status === 'Approved';

  const approve = async () => {
    if (result?.id == null) return;
    setConfirmOk(false);
    await run(() => docInterpretationService.approve(result.id!, arrivalRegistration));
  };

  const documentTypeText = (type: string | null) =>
    type === 'Invoice' ? t(`${p}.upload.documentTypeInvoice`, {}, 'Faktura')
      : type === 'PackingSlip' ? t(`${p}.upload.documentTypePackingSlip`, {}, 'Pakkseddel')
      : t(`${p}.overview.documentTypeOther`, {}, 'Dokument');

  const checkText = (check: string) => t(`${p}.checks.${check}`, {}, check);
  const orderLabel = (orderNo: number, orderNoERP?: string | null) =>
    orderNoERP && orderNoERP !== String(orderNo) ? `${orderNoERP} (${orderNo})` : String(orderNo);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4">
          <BackButton onClick={onClose} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <FileText size={24} />
            <span>{t(`${p}.title`, {}, 'Les inn leverandørdokument')}</span>
          </h1>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-center gap-2">
          <AlertCircle size={16} />{error}
        </div>
      )}

      {/* Åpnes et tolket dokument, vises ikke opplastingsskjemaet mens det lastes */}
      {!settings || (initialResultId != null && !result && !error) ? (
        <div className="flex items-center justify-center h-64"><Loader className="animate-spin" size={20} /></div>
      ) : !result ? (
        <DocUploadForm
          preselectedOrders={preselectedOrders}
          enabled={settings.enabled}
          busy={busy}
          onSubmit={(file, documentType, orderNos) => interpret(file, documentType, orderNos)}
        />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-4 p-4 overflow-auto lg:overflow-hidden">
          {/* Venstre: oversikt og linjer */}
          <div className="lg:w-1/2 lg:min-h-0 space-y-3 lg:overflow-auto">
            {/* Tolkingen tar gjerne 20–40 sekunder — vis at noe skjer */}
            {reinterpreting && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center gap-2">
                <Loader className="animate-spin shrink-0" size={16} />
                {t(`${p}.overview.reinterpreting`, {}, 'Tolker dokumentet på nytt. Det kan ta inntil ett minutt.')}
              </div>
            )}
            {result.alreadyInterpreted && lastUpload && !reinterpreting && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center justify-between gap-3">
                <span>{t(`${p}.overview.alreadyInterpreted`, {}, 'Denne filen er tolket før. Du ser den forrige tolkingen.')}</span>
                <button type="button" disabled={busy} onClick={() => interpret(lastUpload.file, lastUpload.documentType, lastUpload.orderNos, true)}
                  className="px-3 py-2 bg-white border border-amber-200 rounded-lg hover:bg-amber-100 flex items-center gap-1 whitespace-nowrap">
                  <RefreshCw size={14} />{t(`${p}.overview.reinterpret`, {}, 'Tolk på nytt')}
                </button>
              </div>
            )}

            {result.status === 'Failed' ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 space-y-2">
                <div className="font-semibold">{t(`${p}.overview.failed`, {}, 'Dokumentet kunne ikke tolkes.')}</div>
                <div>{result.errorMessage}</div>
                {lastUpload && (
                  <button type="button" disabled={busy} onClick={() => interpret(lastUpload.file, lastUpload.documentType, lastUpload.orderNos, true)}
                    className="px-3 py-2 bg-white border border-red-200 rounded-lg hover:bg-red-100">
                    {t(`${p}.overview.retry`, {}, 'Prøv igjen')}
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Oppsummering (punkt 14) */}
                <div className="p-4 bg-white border border-gray-200 rounded-lg space-y-2">
                  <div className="text-lg font-semibold text-gray-800">
                    {documentTypeText(result.documentType)} {result.documentNo ?? t(`${p}.overview.noDocumentNo`, {}, '(uten nummer)')}
                  </div>
                  <div className="text-sm text-gray-600">
                    {result.summary.orders === 1
                      ? t(`${p}.overview.ordersOne`, {}, '1 bestilling')
                      : t(`${p}.overview.ordersMany`, { '0': result.summary.orders }, `${result.summary.orders} bestillinger`)}
                    {' · '}
                    {result.summary.lines === 1
                      ? t(`${p}.overview.linesOne`, {}, '1 linje')
                      : t(`${p}.overview.linesMany`, { '0': result.summary.lines }, `${result.summary.lines} linjer`)}
                    {' · '}
                    {t(`${p}.overview.summaryStatus`, { '0': result.summary.ready, '1': result.summary.decide, '2': result.summary.notFound },
                      `${result.summary.ready} klar · ${result.summary.decide} må avgjøres · ${result.summary.notFound} ikke funnet`)}
                    {result.summary.batchUncertain > 0 && ` · ${t(`${p}.overview.batchUncertainCount`, { '0': result.summary.batchUncertain }, `${result.summary.batchUncertain} usikker batch`)}`}
                    {result.summary.excluded > 0 && ` · ${t(`${p}.overview.excludedCount`, { '0': result.summary.excluded }, `${result.summary.excluded} holdt utenfor`)}`}
                  </div>
                  <div className="text-xs text-gray-500">
                    {result.method === 'Script'
                      ? t(`${p}.overview.methodScript`, {}, 'Tolket med skriptmal')
                      : t(`${p}.overview.methodAi`, { '0': result.aiModel ?? '' }, `Tolket med AI (${result.aiModel ?? ''})`)}
                    {result.scriptAttempted && result.method !== 'Script' && ` · ${t(`${p}.overview.scriptFailed`, {}, 'skriptmalen feilet')}`}
                  </div>
                </div>

                {/* Leverandør (punkt 7) */}
                <div className="p-4 bg-white border border-gray-200 rounded-lg space-y-2 text-sm">
                  <div className="font-semibold text-gray-800">{t(`${p}.overview.supplier`, {}, 'Leverandør')}</div>
                  <div className="text-gray-600">
                    {result.supplier.documentSupplierName ?? '–'}
                    {result.supplier.vatNo && ` · ${result.supplier.vatNo}`}
                    {result.supplier.orgNo && ` · ${result.supplier.orgNo}`}
                  </div>
                  {!result.supplier.supplierNo && (
                    <div className="text-amber-800">{t(`${p}.overview.supplierUnknown`, {}, 'Leverandøren ble ikke gjenkjent. Skriv inn leverandørnummeret.')}</div>
                  )}
                  {result.status === 'Interpreted' && (
                    <div className="flex gap-2">
                      <ScanTextInput value={supplierInput} onChange={e => setSupplierInput(e.target.value)}
                        placeholder={t(`${p}.overview.supplierNo`, {}, 'Leverandørnr.')}
                        className="w-40 px-3 py-2 border border-gray-300 rounded-lg" />
                      <button type="button"
                        disabled={busy || !supplierInput.trim() || supplierInput.trim() === result.supplier.supplierNo}
                        onClick={() => run(() => docInterpretationService.correctSupplier(result.id!, supplierInput.trim()))}
                        className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:text-gray-400">
                        {t(`${p}.overview.correctSupplier`, {}, 'Rett leverandør')}
                      </button>
                      {result.supplier.supplierName && <span className="self-center text-gray-600">{result.supplier.supplierName}</span>}
                    </div>
                  )}
                </div>

                {(result.warnings.length > 0 || result.missingOrderReferences.length > 0) && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 space-y-1">
                    {result.missingOrderReferences.length > 0 && (
                      <div className="flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5" />
                        {t(`${p}.overview.missingOrders`, { '0': result.missingOrderReferences.join(', ') }, `Bestillinger som ikke finnes: ${result.missingOrderReferences.join(', ')}`)}
                      </div>
                    )}
                    {(showAllWarnings ? result.warnings : result.warnings.slice(0, 2)).map((w, i) =>
                      <div key={i} className="flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" />{w}</div>)}
                    {result.warnings.length > 2 && (
                      <button type="button" onClick={() => setShowAllWarnings(!showAllWarnings)} className="text-xs font-semibold underline">
                        {showAllWarnings
                          ? t(`${p}.overview.showFewerWarnings`, {}, 'Vis færre')
                          : t(`${p}.overview.showAllWarnings`, { '0': result.warnings.length }, `Vis alle varsler (${result.warnings.length})`)}
                      </button>
                    )}
                  </div>
                )}

                {/* Linjer per bestilling */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  {groups.map(group => (
                    <div key={group.orderNo ?? 'none'}>
                      <div className="px-3 py-2 bg-gray-100 text-xs font-semibold text-gray-600 uppercase">
                        {group.orderNo != null
                          ? t(`${p}.overview.order`, { '0': orderLabel(group.orderNo, group.lines[0]?.orderNoERP) },
                              `Bestilling ${orderLabel(group.orderNo, group.lines[0]?.orderNoERP)}`)
                          : t(`${p}.overview.noOrder`, {}, 'Uten bestilling')}
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          {group.lines.map(line => (
                            <tr key={line.id} onClick={() => selectLine(line)}
                              className={`border-t border-gray-200 cursor-pointer ${line.id === selectedLineId ? 'bg-blue-50' : 'hover:bg-gray-50'} ${line.excluded ? 'opacity-60' : ''}`}>
                              <td className="px-3 py-2 w-32">
                                <span className={`px-2 py-0.5 text-xs font-semibold border rounded-full whitespace-nowrap ${statusClasses(line.status)}`}>
                                  {t(`${p}.status.${line.status}`, {}, line.status)}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <div className="font-medium text-gray-800">{line.itemNo ?? line.docSupplierItemNo ?? '–'}</div>
                                <div className="text-xs text-gray-500 truncate max-w-xs">{line.docDescription}</div>
                                {openChecks(line).length > 0 && (
                                  <div className="text-xs text-gray-600">
                                    {openChecks(line).map(c => c === 'Conflict' && line.conflictType
                                      ? t(`${p}.overview.conflictTypes.${line.conflictType}`, {}, checkText(c))
                                      : checkText(c)).join(' · ')}
                                  </div>
                                )}
                                {/* Valgene innkjøper har gjort — OK kan ikke angres */}
                                {lineChoices(line).length > 0 && (
                                  <div className="text-xs text-blue-800">
                                    {lineChoices(line).map(c => t(`${p}.choices.${c.key}`, c.params, c.key)).join(' · ')}
                                  </div>
                                )}
                                {line.serialItem && !line.excluded && (
                                  <div className="text-xs text-gray-600">
                                    {t(`${p}.overview.serialNoArrival`, {}, 'Serienummervare: ankomstregistreres ikke')}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                <div>{fmt(effectiveQuantity(line))} {line.docUnit ?? ''}</div>
                                {/* Bestilt antall på bestillingslinjen, og hva som allerede er mottatt */}
                                {line.orderedQuantity != null && (
                                  <div className="text-xs text-gray-500">
                                    {line.receivedQuantity
                                      ? t(`${p}.overview.orderedReceived`, { '0': fmt(line.orderedQuantity), '1': fmt(line.receivedQuantity) },
                                          `Bestilt ${fmt(line.orderedQuantity)} · mottatt ${fmt(line.receivedQuantity)}`)
                                      : t(`${p}.overview.ordered`, { '0': fmt(line.orderedQuantity) }, `Bestilt ${fmt(line.orderedQuantity)}`)}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                                <div>{line.docBatchNo ?? ''}</div>
                                {/* Varen er ikke batchstyrt: batchen fra dokumentet registreres ikke */}
                                {line.docBatchNo && line.itemNo && !line.batchItem && (
                                  <div className="text-gray-400">{t(`${p}.overview.batchNotUsed`, {}, 'brukes ikke')}</div>
                                )}
                                {line.docExpiryDate && <div>{fmtDate(line.docExpiryDate)}</div>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>

                {/* OK (punkt 20–21) */}
                <div className="p-4 bg-white border border-gray-200 rounded-lg space-y-3 text-sm">
                  <div className="font-semibold text-gray-800">{t(`${p}.approve.actions`, {}, 'Dette skjer ved OK')}</div>
                  <ul className="list-disc ml-5 text-gray-700 space-y-1">
                    {settings.writeInboundShipmentNo && (
                      <li>{t(`${p}.approve.writeInboundShipmentNo`, { '0': result.documentNo ?? '' }, `Dokumentnummer ${result.documentNo ?? ''} skrives på bestillingslinjene dokumentet gjelder`)}</li>
                    )}
                    <li className="flex items-center gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={arrivalRegistration} disabled={approved || busy}
                          onChange={e => setArrivalOverride(e.target.checked)} className="w-5 h-5" />
                        {t(`${p}.approve.arrivalRegistration`, {}, 'Ankomstregistrer linjene')}
                      </label>
                    </li>
                    {settings.batchDistribution && (
                      <li>{t(`${p}.approve.batchDistribution`, {}, 'Én mottakslinje per batch; linjer med batch ankomstregistreres alltid')}</li>
                    )}
                    <li>{t(`${p}.approve.learnMappings`, {}, 'Manuelle koblinger huskes for leverandøren')}</li>
                  </ul>
                  {!result.allDecided && result.status === 'Interpreted' && (
                    <div className="text-amber-800">{t(`${p}.approve.notDecided`, {}, 'OK er sperret til alle linjer som må avgjøres eller ikke er funnet, er avgjort.')}</div>
                  )}
                  {detailDirty && !approved && (
                    <div className="text-amber-800">{t(`${p}.approve.unsavedReceiptLines`, {}, 'Mottakslinjene har ulagrede endringer. Lagre dem før OK.')}</div>
                  )}
                  {result.allDecided && result.status === 'Interpreted' && approvalSummary(result).included === 0 && (
                    <div className="text-amber-800">{t(`${p}.approve.nothingIncluded`, {}, 'Alle linjene er holdt utenfor. Det er ingenting å godkjenne.')}</div>
                  )}
                </div>

                {/* OK står fast nederst i kolonnen, så den er synlig uten å rulle forbi alle linjene */}
                <div className="lg:sticky lg:bottom-0 z-10 p-3 bg-white border border-gray-200 rounded-lg text-sm shadow-sm">
                  {approved ? (
                    <div className="flex items-start gap-2 p-3 rounded-lg border bg-green-50 text-green-800 border-green-200">
                      <CheckCircle size={16} className="mt-0.5 shrink-0" />
                      <div>
                        <div className="font-semibold">
                          {t(`${p}.approve.approvedBy`, { '0': result.approvedBy ?? '', '1': fmtDateTime(result.approvedAt) },
                            `Godkjent av ${result.approvedBy ?? ''} ${fmtDateTime(result.approvedAt)}`)}
                        </div>
                        <div>{t(`${p}.approve.approvedHelp`, {}, 'Lageret kan ta imot på dokumentnummeret i varemottaket.')}</div>
                      </div>
                    </div>
                  ) : confirmOk ? (
                    /* OK er endelig — angre finnes ikke i første versjon (TW 36423839) */
                    <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 space-y-2">
                      <div className="text-gray-800">
                        {t(`${p}.approve.confirmSummary`,
                          { '0': approvalSummary(result).included, '1': approvalSummary(result).excluded, '2': fmt(approvalSummary(result).quantity) },
                          `Linjer med: ${approvalSummary(result).included} · holdt utenfor: ${approvalSummary(result).excluded} · enheter: ${fmt(approvalSummary(result).quantity)}`)}
                        {' · '}
                        {arrivalRegistration
                          ? t(`${p}.approve.confirmArrivalOn`, {}, 'ankomstregistreres')
                          : t(`${p}.approve.confirmArrivalOff`, {}, 'ankomstregistreres ikke (bare linjer med batch)')}
                      </div>
                      {arrivalRegistration && approvalSummary(result).serialLines > 0 && (
                        <div className="text-gray-800">
                          {t(`${p}.approve.confirmSerialLines`, { '0': approvalSummary(result).serialLines },
                            `${approvalSummary(result).serialLines} linjer med serienummervarer ankomstregistreres ikke. Serienumrene registreres i varemottaket.`)}
                        </div>
                      )}
                      {approvalSummary(result).newOrderLines > 0 && (
                        <div className="font-semibold text-gray-800">
                          {t(`${p}.approve.confirmNewOrderLines`, { '0': approvalSummary(result).newOrderLines },
                            `${approvalSummary(result).newOrderLines} nye bestillingslinjer opprettes.`)}
                        </div>
                      )}
                      <div className="text-amber-800">{t(`${p}.approve.confirm`, {}, 'OK kan ikke angres. Vil du godkjenne dokumentet?')}</div>
                      <div className="flex gap-2">
                        <button type="button" disabled={busy} onClick={approve}
                          className="px-4 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                          {t(`${p}.approve.confirmYes`, {}, 'Ja, godkjenn')}
                        </button>
                        <button type="button" disabled={busy} onClick={() => setConfirmOk(false)}
                          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                          {t(`${p}.approve.confirmNo`, {}, 'Avbryt')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" disabled={!canApprove(result) || busy || detailDirty} onClick={() => setConfirmOk(true)}
                      className={`w-full px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
                        canApprove(result) && !detailDirty ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
                      <CheckCircle size={16} />{t(`${p}.approve.ok`, {}, 'OK')}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Høyre: originaldokumentet og valgt linje (punkt 15) */}
          <div className="lg:w-1/2 flex flex-col min-h-[32rem] lg:min-h-0 gap-3">
            {/* Valgt linje: PDF og linjedetalj deler høyden, så avgjørelsene får plass */}
            <div className={`${selectedLine ? 'lg:flex-[1_1_45%]' : 'flex-1'} min-h-[14rem] bg-white border border-gray-200 rounded-lg overflow-hidden`}>
              {pdf ? (
                <PdfPageView file={pdf} page={page} onPageChange={setPage}
                  highlight={selectedLine && selectedLine.pageNo === page ? parseRegion(selectedLine.regionJson) : null}
                  anchors={selectedLine && selectedLine.pageNo === page ? lineAnchors(selectedLine, result.lines) : undefined} />
              ) : (
                <div className="flex items-center justify-center h-full"><Loader className="animate-spin text-gray-400" size={20} /></div>
              )}
            </div>
            {selectedLine && result.status !== 'Failed' && (
              <div className="lg:flex-[1_1_55%] lg:min-h-0 bg-white border border-gray-200 rounded-lg p-4 overflow-auto">
                <DocLineDetail result={result} line={selectedLine} writesInboundShipmentNo={settings.writeInboundShipmentNo} run={run} busy={busy}
                  onDirtyChange={setDetailDirty} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
